import React, { useEffect, useState } from 'react';
import MetricCard, { MetricCardGroup } from './MetricCard';
import Panel from './Panel';
import DataTable from './DataTable';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';
import useAutomationCoverageByEpic from '../../hooks/useAutomationCoverageByEpic';

const JIRA_BASE = 'https://contentsquare.atlassian.net/browse';

const EPIC_COLUMNS = [
  { key: 'productGroup', label: 'Product Group' },
  { key: 'productUnit', label: 'Product Unit' },
  { 
    key: 'epicKey', 
    label: 'Epic Key', 
    render: r => <a href={`${JIRA_BASE}/${r.epicKey}`} target="_blank" rel="noopener noreferrer">{r.epicKey}</a> 
  },
  { key: 'epicSummary', label: 'Summary' },
  { key: 'epicStatus', label: 'Epic Status' },
  { key: 'reporter', label: 'Reporter' },
  { key: 'testCasesCreated', label: '#Test cases created' },
  { key: 'testCasesReviewedPercent', label: '#Test cases reviewed', render: r => `${r.testCasesReviewedPercent}%` },
  { key: 'automationCoveragePercent', label: 'Automation coverage', render: r => `${r.automationCoveragePercent}%` },
];

function ThemeMetrics({ theme, tone }) {
  return (
    <MetricCardGroup title={`Theme: ${theme.label}`}>
      <MetricCard label="Product Unit" value={theme.data.productUnitCount} tone={tone} />
      <MetricCard label="#Epics" value={theme.data.epicCount} tone={tone} />
      <MetricCard label="#TC Created" value={theme.data.testCasesCreated} tone={tone} info="Test cases created, excluding Quality Team members" />
      <MetricCard label="#TC Reviewed" value={theme.data.testCasesReviewedPct} suffix="%" tone={tone} info="% of test cases with status = verified" />
      <MetricCard label="Automation Coverage" value={theme.data.automationCoveragePct} suffix="%" tone={tone} />
    </MetricCardGroup>
  );
}

const EMPTY_SCOPE_METRICS = { productUnitCount: 0, epicCount: 0, testCasesCreated: 0, testCasesReviewedPct: 0, automationCoveragePct: 0 };

function AutomationCoverageTab({ filters }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [pageAll, setPageAll] = useState(1);
  const [pageGa, setPageGa] = useState(1);
  const pageSize = 50;

  // Fetch data for both scopes
  const { data: dataAll, loading: loadingAll, error: errorAll } = useAutomationCoverageByEpic(filters, 'all', pageAll, pageSize);
  const { data: dataGa, loading: loadingGa, error: errorGa } = useAutomationCoverageByEpic(filters, 'ga-or-pending-release-train', pageGa, pageSize);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (Array.isArray(v)) v.forEach(x => params.append(k, x));
      else if (v) params.append(k, v);
    });
    const qs = params.toString() ? `?${params.toString()}` : '';

    api.get(`${QUALITY_SERVICE_API_URL}/hub/automation-coverage/summary${qs}`)
      .then(r => { if (!cancelled) { setSummary(r.data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError(true); setLoading(false); } });

    return () => { cancelled = true; };
  }, [filters]);

  if (loading && summary === null) return <div className="section-loading">Loading…</div>;

  const allEpics = summary?.allEpics || EMPTY_SCOPE_METRICS;
  const gaOrPendingReleaseTrain = summary?.gaOrPendingReleaseTrain || EMPTY_SCOPE_METRICS;

  const rowsAll = dataAll?.data ?? [];
  const totalAll = dataAll?.total ?? 0;
  const totalPagesAll = totalAll > 0 ? Math.ceil(totalAll / pageSize) : 0;

  const rowsGa = dataGa?.data ?? [];
  const totalGa = dataGa?.total ?? 0;
  const totalPagesGa = totalGa > 0 ? Math.ceil(totalGa / pageSize) : 0;

  return (
    <div className="qk-tab-content">
      {error && <div className="section-error">Couldn't load Automation Coverage metrics — showing 0s.</div>}
      <Panel accent="amber">
        <ThemeMetrics theme={{ label: 'All Epics', data: allEpics }} tone="amber" />
      </Panel>
      <Panel accent="green">
        <ThemeMetrics theme={{ label: 'Epics Which are already in GA or Pending Release Train', data: gaOrPendingReleaseTrain }} tone="green" />
      </Panel>

      <Panel title="Test Case Adoption by Epic" accent="amber">
        {errorAll && <div className="section-error">Couldn't load epic data.</div>}
        {loadingAll && <div className="section-loading">Loading…</div>}
        {!loadingAll && !errorAll && rowsAll.length > 0 && (
          <>
            <DataTable columns={EPIC_COLUMNS} rows={rowsAll} ariaLabel="Test Case Adoption by Epic" />
            {totalAll > 0 && (
              <div className="pagination-controls" style={{ marginTop: '16px', textAlign: 'center', fontSize: '14px' }}>
                <button onClick={() => setPageAll(p => Math.max(1, p - 1))} disabled={pageAll === 1}>← Previous</button>
                <span style={{ margin: '0 8px' }}>Page {pageAll} of {totalPagesAll} ({totalAll} total)</span>
                <button onClick={() => setPageAll(p => Math.min(totalPagesAll, p + 1))} disabled={pageAll === totalPagesAll}>Next →</button>
              </div>
            )}
          </>
        )}
        {!loadingAll && !errorAll && rowsAll.length === 0 && <div style={{ padding: '16px' }}>No epics found.</div>}
      </Panel>

      <Panel title="Test Case Adoption by Epic — GA or Pending Release Train" accent="green">
        {errorGa && <div className="section-error">Couldn't load epic data.</div>}
        {loadingGa && <div className="section-loading">Loading…</div>}
        {!loadingGa && !errorGa && rowsGa.length > 0 && (
          <>
            <DataTable columns={EPIC_COLUMNS} rows={rowsGa} ariaLabel="Test Case Adoption by Epic — GA or Pending Release Train" />
            {totalGa > 0 && (
              <div className="pagination-controls" style={{ marginTop: '16px', textAlign: 'center', fontSize: '14px' }}>
                <button onClick={() => setPageGa(p => Math.max(1, p - 1))} disabled={pageGa === 1}>← Previous</button>
                <span style={{ margin: '0 8px' }}>Page {pageGa} of {totalPagesGa} ({totalGa} total)</span>
                <button onClick={() => setPageGa(p => Math.min(totalPagesGa, p + 1))} disabled={pageGa === totalPagesGa}>Next →</button>
              </div>
            )}
          </>
        )}
        {!loadingGa && !errorGa && rowsGa.length === 0 && <div style={{ padding: '16px' }}>No epics found.</div>}
      </Panel>
    </div>
  );
}

export default AutomationCoverageTab;
